import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  TrendingUp,
  TrendingDown,
  Brain,
  BarChart3,
  PieChart,
  Briefcase,
  Building2,
  Calendar,
  DollarSign,
  ExternalLink,
  FileText,
  Award,
  Target,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import './InsiderProfilePage.css';

// =============================================================================
// MOCK DATA — keyed by insider slug (matches route param)
// =============================================================================

const insiderData = {
  'jensen-huang': {
    name: 'Jensen Huang', title: 'CEO & President', company: 'NVIDIA Corporation', ticker: 'NVDA',
    photo: null,
    netWorth: '$108B', totalTradeVolume: '$892M', totalTrades: 47, avgTradeSize: '$18.9M',
    buyCount: 3, sellCount: 44, winRate: 82, avgReturn: -1.4,
    ownerType: 'Officer', tenure: '1993 - Present', age: 62,
    bio: 'Co-founder and CEO of NVIDIA since 1993. Under his leadership, NVIDIA became the dominant force in GPU computing and AI infrastructure.',
    sectors: [
      { name: 'Technology', pct: 100, color: '#8b5cf6' },
    ],
    yearlyActivity: [
      { year: '2021', buys: 0, sells: 8, volume: 142000000 },
      { year: '2022', buys: 1, sells: 10, volume: 198000000 },
      { year: '2023', buys: 0, sells: 12, volume: 245000000 },
      { year: '2024', buys: 1, sells: 8, volume: 178000000 },
      { year: '2025', buys: 1, sells: 4, volume: 112000000 },
      { year: '2026', buys: 0, sells: 2, volume: 17100000 },
    ],
    performanceVsSP: '+812%',
    strategyReturn: '+4,200%',
    sharpe: 2.1,
    maxDrawdown: '-18.4%',
    trades: [
      { id: 1, ticker: 'NVDA', company: 'NVIDIA Corporation', type: 'Sale', shares: 120000, price: 142.50, value: 17100000, filed: '2026-02-07', traded: '2026-02-05', returnSince: -2.1 },
      { id: 2, ticker: 'NVDA', company: 'NVIDIA Corporation', type: 'Sale', shares: 90000, price: 138.20, value: 12438000, filed: '2026-01-15', traded: '2026-01-12', returnSince: 3.1 },
      { id: 3, ticker: 'NVDA', company: 'NVIDIA Corporation', type: 'Purchase', shares: 25000, price: 108.40, value: 2710000, filed: '2025-09-20', traded: '2025-09-18', returnSince: 31.4 },
      { id: 4, ticker: 'NVDA', company: 'NVIDIA Corporation', type: 'Sale', shares: 200000, price: 125.60, value: 25120000, filed: '2025-06-10', traded: '2025-06-08', returnSince: 13.5 },
      { id: 5, ticker: 'NVDA', company: 'NVIDIA Corporation', type: 'Sale', shares: 150000, price: 118.90, value: 17835000, filed: '2025-03-22', traded: '2025-03-20', returnSince: 19.8 },
      { id: 6, ticker: 'NVDA', company: 'NVIDIA Corporation', type: 'Sale', shares: 180000, price: 98.40, value: 17712000, filed: '2024-11-05', traded: '2024-11-03', returnSince: 44.8 },
      { id: 7, ticker: 'NVDA', company: 'NVIDIA Corporation', type: 'Purchase', shares: 50000, price: 42.80, value: 2140000, filed: '2022-10-15', traded: '2022-10-12', returnSince: 232.9 },
    ],
    aiInsight: "Jensen Huang's selling pattern is consistent with a 10b5-1 pre-arranged plan — scheduled quarterly dispositions averaging $15-25M. His rare purchases (2022, 2025) correlate with significant product launches. The 2022 buy preceded the ChatGPT-driven AI rally by 2 months. Current selling pace is actually below his 5-year average, which could be interpreted as mild bullishness.",
  },
  'kate-johnson': {
    name: 'Kate Johnson', title: 'President & CEO', company: 'Lumen Technologies', ticker: 'LUMN',
    photo: null,
    netWorth: '$42M', totalTradeVolume: '$8.2M', totalTrades: 12, avgTradeSize: '$683K',
    buyCount: 9, sellCount: 3, winRate: 78, avgReturn: 12.4,
    ownerType: 'Officer', tenure: '2023 - Present', age: 56,
    bio: 'CEO of Lumen Technologies since 2023. Previously held leadership roles at Microsoft and GE Digital. Known for aggressive insider buying during her tenure.',
    sectors: [
      { name: 'Telecom', pct: 85, color: '#3b82f6' },
      { name: 'Technology', pct: 15, color: '#8b5cf6' },
    ],
    yearlyActivity: [
      { year: '2023', buys: 3, sells: 0, volume: 1800000 },
      { year: '2024', buys: 4, sells: 2, volume: 3200000 },
      { year: '2025', buys: 1, sells: 1, volume: 2100000 },
      { year: '2026', buys: 1, sells: 0, volume: 615115 },
    ],
    performanceVsSP: '-12%',
    strategyReturn: '+34%',
    sharpe: 0.9,
    maxDrawdown: '-42.1%',
    trades: [
      { id: 1, ticker: 'LUMN', company: 'Lumen Technologies', type: 'Purchase', shares: 78685, price: 7.82, value: 615115, filed: '2026-02-05', traded: '2026-02-03', returnSince: 8.7 },
      { id: 2, ticker: 'LUMN', company: 'Lumen Technologies', type: 'Purchase', shares: 50000, price: 6.40, value: 320000, filed: '2025-08-12', traded: '2025-08-10', returnSince: 22.2 },
      { id: 3, ticker: 'LUMN', company: 'Lumen Technologies', type: 'Sale', shares: 30000, price: 8.10, value: 243000, filed: '2025-05-20', traded: '2025-05-18', returnSince: -3.5 },
      { id: 4, ticker: 'LUMN', company: 'Lumen Technologies', type: 'Purchase', shares: 100000, price: 5.20, value: 520000, filed: '2024-11-15', traded: '2024-11-13', returnSince: 50.4 },
      { id: 5, ticker: 'LUMN', company: 'Lumen Technologies', type: 'Purchase', shares: 65000, price: 4.80, value: 312000, filed: '2024-06-22', traded: '2024-06-20', returnSince: 62.9 },
      { id: 6, ticker: 'LUMN', company: 'Lumen Technologies', type: 'Purchase', shares: 80000, price: 3.90, value: 312000, filed: '2024-02-10', traded: '2024-02-08', returnSince: 100.5 },
      { id: 7, ticker: 'LUMN', company: 'Lumen Technologies', type: 'Purchase', shares: 120000, price: 2.10, value: 252000, filed: '2023-09-05', traded: '2023-09-03', returnSince: 272.4 },
    ],
    aiInsight: "Kate Johnson is one of the strongest insider conviction signals in the market. She has purchased LUMN shares 9 times since becoming CEO, with only 3 scheduled sales. Her average purchase price is $4.60, and LUMN currently trades at $8.50 — representing an 85% average gain. Cluster buying pattern: 4 executives at LUMN have bought in the last 10 days, the strongest insider cluster signal this month.",
  },
  'tim-cook': {
    name: 'Tim Cook', title: 'CEO', company: 'Apple Inc.', ticker: 'AAPL',
    photo: null,
    netWorth: '$1.9B', totalTradeVolume: '$1.2B', totalTrades: 68, avgTradeSize: '$17.6M',
    buyCount: 0, sellCount: 68, winRate: 74, avgReturn: -0.8,
    ownerType: 'Officer', tenure: '2011 - Present', age: 65,
    bio: 'CEO of Apple since 2011, succeeding Steve Jobs. All insider transactions are scheduled 10b5-1 plan dispositions of vested RSUs.',
    sectors: [
      { name: 'Technology', pct: 100, color: '#8b5cf6' },
    ],
    yearlyActivity: [
      { year: '2021', buys: 0, sells: 12, volume: 142000000 },
      { year: '2022', buys: 0, sells: 10, volume: 98000000 },
      { year: '2023', buys: 0, sells: 14, volume: 187000000 },
      { year: '2024', buys: 0, sells: 16, volume: 245000000 },
      { year: '2025', buys: 0, sells: 12, volume: 312000000 },
      { year: '2026', buys: 0, sells: 4, volume: 72000000 },
    ],
    performanceVsSP: '+340%',
    strategyReturn: '+680%',
    sharpe: 1.6,
    maxDrawdown: '-31.2%',
    trades: [
      { id: 1, ticker: 'AAPL', company: 'Apple Inc.', type: 'Sale', shares: 75000, price: 238.20, value: 17865000, filed: '2026-02-06', traded: '2026-02-03', returnSince: 1.3 },
      { id: 2, ticker: 'AAPL', company: 'Apple Inc.', type: 'Sale', shares: 60000, price: 242.10, value: 14526000, filed: '2026-01-10', traded: '2026-01-08', returnSince: -1.6 },
      { id: 3, ticker: 'AAPL', company: 'Apple Inc.', type: 'Sale', shares: 80000, price: 230.80, value: 18464000, filed: '2025-11-20', traded: '2025-11-18', returnSince: 3.2 },
      { id: 4, ticker: 'AAPL', company: 'Apple Inc.', type: 'Sale', shares: 100000, price: 218.40, value: 21840000, filed: '2025-08-15', traded: '2025-08-13', returnSince: 9.1 },
    ],
    aiInsight: "Tim Cook's selling is entirely 10b5-1 plan-based — quarterly vesting of RSU compensation. He has never made a discretionary purchase or sale. This pattern carries minimal signal value for directional analysis. Apple's stock performance should be evaluated through institutional flows and product cycle analysis rather than insider transactions.",
  },
  'vicki-hollub': {
    name: 'Vicki Hollub', title: 'President & CEO', company: 'Occidental Petroleum', ticker: 'OXY',
    photo: null,
    netWorth: '$58M', totalTradeVolume: '$22M', totalTrades: 18, avgTradeSize: '$1.2M',
    buyCount: 12, sellCount: 6, winRate: 83, avgReturn: 9.8,
    ownerType: 'Officer', tenure: '2016 - Present', age: 64,
    bio: 'CEO of Occidental Petroleum since 2016. Known for consistent insider buying, particularly during oil price dips. Berkshire Hathaway (Buffett) is a major OXY shareholder.',
    sectors: [
      { name: 'Energy', pct: 100, color: '#f59e0b' },
    ],
    yearlyActivity: [
      { year: '2021', buys: 2, sells: 1, volume: 2400000 },
      { year: '2022', buys: 3, sells: 2, volume: 4800000 },
      { year: '2023', buys: 2, sells: 1, volume: 3200000 },
      { year: '2024', buys: 3, sells: 1, volume: 5100000 },
      { year: '2025', buys: 1, sells: 1, volume: 3800000 },
      { year: '2026', buys: 1, sells: 0, volume: 1369200 },
    ],
    performanceVsSP: '+180%',
    strategyReturn: '+240%',
    sharpe: 1.4,
    maxDrawdown: '-28.6%',
    trades: [
      { id: 1, ticker: 'OXY', company: 'Occidental Petroleum', type: 'Purchase', shares: 28000, price: 48.90, value: 1369200, filed: '2026-01-26', traded: '2026-01-21', returnSince: 7.2 },
      { id: 2, ticker: 'OXY', company: 'Occidental Petroleum', type: 'Purchase', shares: 35000, price: 52.40, value: 1834000, filed: '2025-07-15', traded: '2025-07-12', returnSince: -6.7 },
      { id: 3, ticker: 'OXY', company: 'Occidental Petroleum', type: 'Sale', shares: 20000, price: 62.10, value: 1242000, filed: '2025-03-10', traded: '2025-03-08', returnSince: -21.2 },
      { id: 4, ticker: 'OXY', company: 'Occidental Petroleum', type: 'Purchase', shares: 40000, price: 45.80, value: 1832000, filed: '2024-10-20', traded: '2024-10-18', returnSince: 6.8 },
    ],
    aiInsight: "Vicki Hollub is a classic conviction buyer — she buys OXY on dips and her purchases have a 72% 90-day hit rate. Her latest buy at $48.90 aligns with Berkshire Hathaway's ongoing accumulation (8.4M additional shares this quarter). When both Hollub and Buffett are buying simultaneously, OXY has averaged +14% over the following 6 months historically.",
  },
};

// Default for unknown insiders
const defaultInsider = {
  name: 'Unknown Insider', title: 'N/A', company: 'N/A', ticker: 'N/A',
  netWorth: 'N/A', totalTradeVolume: 'N/A', totalTrades: 0, avgTradeSize: 'N/A',
  buyCount: 0, sellCount: 0, winRate: 0, avgReturn: 0,
  ownerType: 'N/A', tenure: 'N/A', age: 0, bio: '',
  sectors: [], yearlyActivity: [], trades: [],
  performanceVsSP: 'N/A', strategyReturn: 'N/A', sharpe: 0, maxDrawdown: 'N/A',
  aiInsight: 'No data available for this insider.',
};

// =============================================================================
// HELPERS
// =============================================================================

const formatCurrency = (value) => {
  if (typeof value === 'string') return value;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatShares = (num) => {
  if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(0)}K`;
  return num.toLocaleString();
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

function InsiderProfilePage() {
  const { insiderId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('trades');
  const [currentPage, setCurrentPage] = useState(1);
  const tradesPerPage = 8;

  const insider = insiderData[insiderId] || {
    ...defaultInsider,
    name: insiderId?.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || 'Unknown'
  };

  const totalPages = Math.ceil(insider.trades.length / tradesPerPage);
  const paginatedTrades = insider.trades.slice(
    (currentPage - 1) * tradesPerPage,
    currentPage * tradesPerPage
  );

  const maxVolume = Math.max(...(insider.yearlyActivity.map(y => y.volume) || [1]), 1);

  return (
    <div className="insider-profile-page">
      {/* Header */}
      <div className="ip-header">
        <button className="ip-back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={18} />
          Back
        </button>
      </div>

      {/* Main Layout */}
      <div className="ip-layout">
        {/* Left Sidebar - Profile */}
        <div className="ip-sidebar">
          {/* Profile Card */}
          <div className="ip-profile-card">
            <div className="ip-avatar">
              <div className={`ip-avatar-circle ${insider.buyCount > insider.sellCount ? 'bullish' : 'bearish'}`}>
                <span>{insider.name.split(' ').map(n => n[0]).join('')}</span>
              </div>
            </div>
            <h1 className="ip-name">{insider.name}</h1>
            <p className="ip-title-text">{insider.title}</p>
            <button className="ip-company-link" onClick={() => navigate(`/stock/${insider.ticker}`)}>
              <Building2 size={14} />
              {insider.company} ({insider.ticker})
              <ExternalLink size={12} />
            </button>

            <div className="ip-meta-grid">
              <div className="ip-meta-item">
                <span className="ip-meta-label">Role</span>
                <span className="ip-meta-value">{insider.ownerType}</span>
              </div>
              <div className="ip-meta-item">
                <span className="ip-meta-label">Tenure</span>
                <span className="ip-meta-value">{insider.tenure}</span>
              </div>
              <div className="ip-meta-item">
                <span className="ip-meta-label">Age</span>
                <span className="ip-meta-value">{insider.age || 'N/A'}</span>
              </div>
              <div className="ip-meta-item">
                <span className="ip-meta-label">Net Worth</span>
                <span className="ip-meta-value">{insider.netWorth}</span>
              </div>
            </div>

            {insider.bio && (
              <p className="ip-bio">{insider.bio}</p>
            )}
          </div>

          {/* Trading Stats Card */}
          <div className="ip-stats-card">
            <h3 className="ip-card-title"><BarChart3 size={16} /> Trading Overview</h3>
            <div className="ip-stat-rows">
              <div className="ip-stat-row">
                <span>Total Trade Volume</span>
                <span className="ip-stat-val">{insider.totalTradeVolume}</span>
              </div>
              <div className="ip-stat-row">
                <span>Total Transactions</span>
                <span className="ip-stat-val">{insider.totalTrades}</span>
              </div>
              <div className="ip-stat-row">
                <span>Avg Trade Size</span>
                <span className="ip-stat-val">{insider.avgTradeSize}</span>
              </div>
              <div className="ip-stat-row">
                <span>Buys / Sells</span>
                <span className="ip-stat-val">
                  <span style={{ color: '#10b981' }}>{insider.buyCount}</span>
                  {' / '}
                  <span style={{ color: '#ef4444' }}>{insider.sellCount}</span>
                </span>
              </div>
              <div className="ip-stat-row">
                <span>Win Rate</span>
                <span className={`ip-stat-val ${insider.winRate >= 50 ? 'positive' : 'negative'}`}>{insider.winRate}%</span>
              </div>
              <div className="ip-stat-row">
                <span>Avg Return</span>
                <span className={`ip-stat-val ${insider.avgReturn >= 0 ? 'positive' : 'negative'}`}>
                  {insider.avgReturn >= 0 ? '+' : ''}{insider.avgReturn}%
                </span>
              </div>
            </div>
          </div>

          {/* Performance Card */}
          <div className="ip-stats-card">
            <h3 className="ip-card-title"><Award size={16} /> Performance</h3>
            <div className="ip-stat-rows">
              <div className="ip-stat-row">
                <span>Strategy Return</span>
                <span className="ip-stat-val highlight">{insider.strategyReturn}</span>
              </div>
              <div className="ip-stat-row">
                <span>vs S&P 500</span>
                <span className={`ip-stat-val ${insider.performanceVsSP?.startsWith('+') ? 'positive' : 'negative'}`}>
                  {insider.performanceVsSP}
                </span>
              </div>
              <div className="ip-stat-row">
                <span>Sharpe Ratio</span>
                <span className="ip-stat-val">{insider.sharpe}</span>
              </div>
              <div className="ip-stat-row">
                <span>Max Drawdown</span>
                <span className="ip-stat-val negative">{insider.maxDrawdown}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Content */}
        <div className="ip-content">
          {/* AI Insight */}
          <div className="ip-ai-insight">
            <div className="ip-ai-header">
              <span className="ip-ai-badge"><Brain size={12} /> AI INSIGHT</span>
            </div>
            <p>{insider.aiInsight}</p>
          </div>

          {/* Tabs */}
          <div className="ip-tabs">
            <button className={`ip-tab ${activeTab === 'trades' ? 'active' : ''}`} onClick={() => setActiveTab('trades')}>
              <FileText size={15} /> Trade History
            </button>
            <button className={`ip-tab ${activeTab === 'activity' ? 'active' : ''}`} onClick={() => setActiveTab('activity')}>
              <BarChart3 size={15} /> Activity Chart
            </button>
            <button className={`ip-tab ${activeTab === 'sectors' ? 'active' : ''}`} onClick={() => setActiveTab('sectors')}>
              <PieChart size={15} /> Sector Exposure
            </button>
          </div>

          {/* Tab Content */}
          <div className="ip-tab-content">
            {/* TRADES TAB */}
            {activeTab === 'trades' && (
              <div className="ip-trades-panel">
                <table className="ip-trades-table">
                  <thead>
                    <tr>
                      <th>Stock</th>
                      <th>Type</th>
                      <th>Shares</th>
                      <th>Price</th>
                      <th>Value</th>
                      <th>Filed</th>
                      <th>Return</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTrades.map(trade => (
                      <tr key={trade.id}>
                        <td>
                          <span className="ip-trade-ticker">{trade.ticker}</span>
                          <span className="ip-trade-company">{trade.company}</span>
                        </td>
                        <td>
                          <span className={`ip-trade-type ${trade.type === 'Purchase' ? 'buy' : 'sell'}`}>
                            {trade.type === 'Purchase' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                            {trade.type === 'Purchase' ? 'Buy' : 'Sell'}
                          </span>
                        </td>
                        <td>{formatShares(trade.shares)}</td>
                        <td>${trade.price.toFixed(2)}</td>
                        <td className="ip-trade-value">{formatCurrency(trade.value)}</td>
                        <td className="ip-trade-date">{formatDate(trade.filed)}</td>
                        <td>
                          <span className={`ip-trade-return ${trade.returnSince >= 0 ? 'positive' : 'negative'}`}>
                            {trade.returnSince >= 0 ? '+' : ''}{trade.returnSince}%
                          </span>
                        </td>
                        <td>
                          <button className="ip-stock-btn" onClick={(e) => { e.stopPropagation(); navigate(`/stock/${trade.ticker}`); }} title={`View ${trade.ticker}`}>
                            <ExternalLink size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {totalPages > 1 && (
                  <div className="ip-pagination">
                    <span className="ip-page-info">
                      Page {currentPage} of {totalPages} ({insider.trades.length} trades)
                    </span>
                    <div className="ip-page-btns">
                      <button className="ip-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
                        <ChevronLeft size={14} />
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(num => (
                        <button key={num} className={`ip-page-btn ${currentPage === num ? 'active' : ''}`} onClick={() => setCurrentPage(num)}>
                          {num}
                        </button>
                      ))}
                      <button className="ip-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                        <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ACTIVITY CHART TAB */}
            {activeTab === 'activity' && (
              <div className="ip-activity-panel">
                <h3>Yearly Trading Activity</h3>
                <div className="ip-activity-chart">
                  {insider.yearlyActivity.map((year, i) => (
                    <div className="ip-activity-bar-group" key={i}>
                      <div className="ip-bar-container">
                        <div className="ip-bar buy" style={{ height: `${Math.max((year.buys / Math.max(...insider.yearlyActivity.map(y => Math.max(y.buys, y.sells)), 1)) * 120, year.buys > 0 ? 8 : 0)}px` }}>
                          {year.buys > 0 && <span>{year.buys}</span>}
                        </div>
                        <div className="ip-bar sell" style={{ height: `${Math.max((year.sells / Math.max(...insider.yearlyActivity.map(y => Math.max(y.buys, y.sells)), 1)) * 120, year.sells > 0 ? 8 : 0)}px` }}>
                          {year.sells > 0 && <span>{year.sells}</span>}
                        </div>
                      </div>
                      <span className="ip-bar-label">{year.year}</span>
                      <span className="ip-bar-volume">{formatCurrency(year.volume)}</span>
                    </div>
                  ))}
                </div>
                <div className="ip-activity-legend">
                  <span><span className="ip-legend-dot buy" /> Buys</span>
                  <span><span className="ip-legend-dot sell" /> Sells</span>
                </div>
              </div>
            )}

            {/* SECTORS TAB */}
            {activeTab === 'sectors' && (
              <div className="ip-sectors-panel">
                <h3>Sector Exposure</h3>
                <div className="ip-sector-bars">
                  {insider.sectors.map((sector, i) => (
                    <div className="ip-sector-row" key={i}>
                      <span className="ip-sector-name">{sector.name}</span>
                      <div className="ip-sector-bar-wrap">
                        <div className="ip-sector-bar-fill" style={{ width: `${sector.pct}%`, background: sector.color }} />
                      </div>
                      <span className="ip-sector-pct">{sector.pct}%</span>
                    </div>
                  ))}
                </div>
                {insider.sectors.length <= 1 && (
                  <p className="ip-sector-note">
                    This insider primarily trades shares of their own company ({insider.ticker}), resulting in concentrated sector exposure.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default InsiderProfilePage;